import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, apiClient } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import GlobalSearch from './GlobalSearch';
import Breadcrumbs from './Breadcrumbs';
import NotificationBell from './NotificationBell';
import { 
  LayoutDashboard, Building2, Users, Bell, Settings, LogOut,
  Menu, X, Heart, ChevronDown, ChevronRight, User, ClipboardCheck, 
  Package, HandHeart, CalendarRange, Brain, BarChart3, Database, 
  FileText, Shield, PanelLeftClose, PanelLeft,
  TrendingUp, Layers, UserPlus, FolderOpen, Boxes, MailQuestion,
  Cog, Link2, GitBranch, Award, Target, ShoppingBag, History,
  HeartHandshake, Megaphone, Mail, Clock, Sparkles, ExternalLink,
  AlertCircle, CheckCircle, Info, MessageSquare, FileEdit, ListChecks
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

// Grouped navigation structure
const NAV_SECTIONS = [
  {
    id: 'dashboards',
    label: 'Dashboards',
    icon: LayoutDashboard,
    color: 'text-blue-600',
    items: [
      { path: '/dashboard', label: 'Visão Geral', icon: LayoutDashboard },
      { path: '/relatorios', label: 'Relatórios', icon: FileText },
      { path: '/comparador-distritos', label: 'Comparador', icon: TrendingUp, badge: 'Novo' },
    ]
  },
  {
    id: 'gestao',
    label: 'Gestão',
    icon: Building2,
    color: 'text-green-600',
    items: [
      { path: '/instituicoes', label: 'Instituições', icon: Building2 },
      { path: '/gestao-pedidos', label: 'Gestão Pedidos', icon: Package },
      { path: '/candidaturas', label: 'Candidaturas', icon: ClipboardCheck, badge: 'Novo' },
      { path: '/validacao-alteracoes', label: 'Validação Alterações', icon: FileEdit, badge: 'Novo' },
      { path: '/admin/notificacoes', label: 'Central Mensagens', icon: MessageSquare },
      { path: '/admin/utilizadores-portal', label: 'Utilizadores Portal', icon: Users },
      { path: '/admin/historico-acessos', label: 'Histórico Acessos', icon: History },
      { path: '/admin/enriquecimento', label: 'Enriquecimento IA', icon: Sparkles },
      { path: '/utentes', label: 'Utentes', icon: Users },
      { path: '/avaliacoes', label: 'Avaliações', icon: ClipboardCheck },
      { path: '/documentos', label: 'Documentos', icon: FolderOpen },
    ]
  },
  {
    id: 'campanhas',
    label: 'Campanhas Atual. Dados',
    icon: CalendarRange,
    color: 'text-purple-600',
    items: [
      { path: '/campanhas', label: 'Gerir Campanhas', icon: CalendarRange },
    ]
  },
  {
    id: 'visitare',
    label: 'Visitare',
    icon: ClipboardCheck,
    color: 'text-cyan-600',
    items: [
      { path: '/visitare/visitas', label: 'Visitas', icon: CalendarRange, badge: 'Novo' },
      { path: '/visitare/visitadores', label: 'Visitadores', icon: Users },
      { path: '/visitare/certificados', label: 'Certificados EA', icon: Award },
      { path: '/visitare/form-builder', label: 'Form Builder', icon: Layers },
    ]
  },
  {
    id: 'recursos',
    label: 'Recursos',
    icon: Package,
    color: 'text-amber-600',
    items: [
      { path: '/recursos', label: 'Stock', icon: Boxes },
      { path: '/recursos/necessidades', label: 'Necessidades', icon: ShoppingBag, badge: 'Novo' },
      { path: '/priorizacao', label: 'Priorização IA', icon: Brain },
      { path: '/admin/analise-inteligente', label: 'Análise IA', icon: Brain, badge: 'Novo' },
    ]
  },
  {
    id: 'voluntariado',
    label: 'Voluntariado',
    icon: HandHeart,
    color: 'text-pink-600',
    items: [
      { path: '/voluntariado', label: 'Voluntários', icon: UserPlus },
      { path: '/admin/missoes', label: 'Missões', icon: Target, badge: 'Novo' },
      { path: '/validacao-voluntarios', label: 'Validação', icon: ClipboardCheck },
      { path: '/certificados', label: 'Certificados', icon: Award },
      { path: '/matching-analytics', label: 'Analytics', icon: BarChart3 },
    ]
  },
  {
    id: 'parceiros',
    label: 'Parceiros',
    icon: Building2,
    color: 'text-indigo-600',
    items: [
      { path: '/parceiros', label: 'Entidades Parceiras', icon: Building2 },
    ]
  },
  {
    id: 'doadores',
    label: 'Doadores',
    icon: HeartHandshake,
    color: 'text-rose-600',
    items: [
      { path: '/doadores', label: 'Gestão de Doadores', icon: HeartHandshake },
      { path: '/doadores/propostas', label: 'Propostas de Contacto', icon: Mail },
      { path: '/doadores/campanhas', label: 'Campanhas', icon: Megaphone },
      { path: '/doadores/analytics', label: 'Analytics', icon: BarChart3 },
      { path: '/admin/cron-doadores', label: 'Cron Automático', icon: Clock, badge: 'Novo' },
    ]
  },
  {
    id: 'alertas-rgpd',
    label: 'Alertas & RGPD',
    icon: Bell,
    color: 'text-red-600',
    items: [
      { path: '/alertas', label: 'Alertas', icon: Bell, badgeCount: true },
      { path: '/rgpd', label: 'RGPD', icon: Shield },
    ]
  },
];

// Bottom section - separated from main navigation
const BOTTOM_SECTION = {
  id: 'configuracoes',
  label: 'Configurações',
  icon: Settings,
  color: 'text-slate-600',
  items: [
    { path: '/configuracoes/definicoes', label: 'Definições', icon: Settings },
    { path: '/admin/utilizadores', label: 'Utilizadores', icon: Users },
    { path: '/configuracoes/workflows', label: 'Workflows', icon: Layers },
    { path: '/configuracoes/valencias', label: 'Respostas Sociais', icon: FileText },
    { path: '/configuracoes/demografia', label: 'Demografia', icon: Users },
    { path: '/configuracoes/listas', label: 'Listas', icon: ListChecks },
    { path: '/admin/audit-log', label: 'Log de Visitadoria', icon: History },
    { path: '/sendys', label: 'Sendys CRM', icon: Database },
  ]
};

export const MainLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['dashboards', 'gestao']);

  // Load sidebar state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('entrajudata_sidebar_collapsed');
    if (saved) setSidebarCollapsed(saved === 'true');
    
    const savedSections = localStorage.getItem('entrajudata_expanded_sections');
    if (savedSections) setExpandedSections(JSON.parse(savedSections));
  }, []);

  // Save sidebar state
  useEffect(() => {
    localStorage.setItem('entrajudata_sidebar_collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('entrajudata_expanded_sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const isItemActive = (path) => location.pathname === path;
  const isSectionActive = (section) => section.items.some(item => location.pathname === item.path);

  return (
    <div className={`min-h-screen bg-slate-50 transition-colors`} data-testid="main-layout">
      {/* Sidebar - Desktop */}
      <aside 
        className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 bg-white border-r border-slate-200 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
        }`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 py-4 border-b border-slate-100 ${sidebarCollapsed ? 'justify-center' : ''}`}>
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0066CC] rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-white" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <span className="text-lg font-bold text-slate-900">ENTRAJUDATA</span>
                <p className="text-xs text-slate-500">Gestão de Dados</p>
              </div>
            )}
          </Link>
        </div>

        {/* Collapse Toggle */}
        <div className={`px-3 py-2 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full justify-center"
            title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {sidebarCollapsed ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5 mr-2" />
                <span>Recolher</span>
              </>
            )}
          </Button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          {NAV_SECTIONS.map((section) => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSections.includes(section.id);
            const isActive = isSectionActive(section);
            
            if (sidebarCollapsed) {
              // Collapsed mode - show only icons with dropdown
              return (
                <DropdownMenu key={section.id}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-center p-2 ${isActive ? 'bg-[#0066CC]/10 text-[#0066CC]' : ''}`}
                      title={section.label}
                    >
                      <SectionIcon className={`w-5 h-5 ${section.color}`} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="start" className="w-48">
                    <DropdownMenuLabel>{section.label}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link 
                            to={item.path}
                            className={isItemActive(item.path) ? 'bg-[#0066CC]/10 text-[#0066CC]' : ''}
                          >
                            <ItemIcon className="w-4 h-4 mr-2" />
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            // Expanded mode - collapsible sections
            return (
              <Collapsible
                key={section.id}
                open={isExpanded}
                onOpenChange={() => toggleSection(section.id)}
              >
                <CollapsibleTrigger asChild>
                  <button
                    className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-slate-100 text-slate-900' 
                        : 'text-slate-600 hover:bg-slate-100:bg-slate-700'
                    }`}
                  >
                    <SectionIcon className={`w-5 h-5 ${section.color}`} />
                    <span className="flex-1 text-left">{section.label}</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 mt-1 space-y-1">
                  {section.items.map((item) => {
                    const ItemIcon = item.icon;
                    const active = isItemActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          active 
                            ? 'bg-[#0066CC]/10 text-[#0066CC] font-medium' 
                            : 'text-slate-600 hover:bg-slate-100:bg-slate-700'
                        }`}
                        data-testid={`nav-${item.path.slice(1)}`}
                      >
                        <ItemIcon className="w-4 h-4" />
                        <span className="flex-1">{item.label}</span>
                        {item.badge && (
                          <Badge className="bg-[#0066CC] text-white text-xs">{item.badge}</Badge>
                        )}
                      </Link>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
          
          {/* Spacer */}
          <div className="flex-grow min-h-4" />
          
          {/* Bottom Section - Configurações */}
          <div className="pt-4 mt-4 border-t border-slate-200">
            {(() => {
              const section = BOTTOM_SECTION;
              const SectionIcon = section.icon;
              const isExpanded = expandedSections.includes(section.id);
              const isActive = section.items.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
              
              if (sidebarCollapsed) {
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`w-full justify-center p-2 ${isActive ? 'bg-[#0066CC]/10 text-[#0066CC]' : ''}`}
                        title={section.label}
                      >
                        <SectionIcon className={`w-5 h-5 ${section.color}`} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" className="w-48">
                      <DropdownMenuLabel>{section.label}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {section.items.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                          <DropdownMenuItem key={item.path} asChild>
                            <Link 
                              to={item.path}
                              className={isItemActive(item.path) ? 'bg-[#0066CC]/10 text-[#0066CC]' : ''}
                            >
                              <ItemIcon className="w-4 h-4 mr-2" />
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return (
                <Collapsible
                  open={isExpanded}
                  onOpenChange={() => toggleSection(section.id)}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`w-full justify-between px-2 py-2 h-auto ${
                        isActive ? 'bg-[#0066CC]/5' : 'hover:bg-slate-100:bg-slate-800'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <SectionIcon className={`w-5 h-5 ${section.color}`} />
                        <span className="text-sm font-medium">{section.label}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-1 mt-1">
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const itemActive = isItemActive(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                            itemActive
                              ? 'bg-[#0066CC]/10 text-[#0066CC] font-medium'
                              : 'text-slate-600 hover:bg-slate-100:bg-slate-800'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4" />
                          <span className="flex-1">{item.label}</span>
                          {item.badge && (
                            <Badge className="bg-[#0066CC] text-white text-xs">{item.badge}</Badge>
                          )}
                        </Link>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            })()}
          </div>
        </nav>
        
        {/* User Menu */}
        <div className={`px-3 py-3 border-t border-slate-100 ${sidebarCollapsed ? 'flex flex-col items-center gap-2' : ''}`}>
          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className={`gap-3 h-auto ${sidebarCollapsed ? 'w-10 h-10 p-0 justify-center' : 'w-full justify-start px-2 py-2'}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#0066CC] flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-white" />
                </div>
                {!sidebarCollapsed && (
                  <>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-slate-900 truncate">{user?.nome || 'Utilizador'}</p>
                      <p className="text-xs text-slate-500 truncate">{user?.role?.nome || 'Admin'}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={sidebarCollapsed ? 'center' : 'end'} className="w-56">
              <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <User className="w-4 h-4 mr-2" />
                Perfil
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="w-4 h-4 mr-2" />
                Definições
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Terminar Sessão
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-slate-900/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl animate-slide-in overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0066CC] rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <span className="text-lg font-bold">ENTRAJUDATA</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Mobile Navigation */}
            <nav className="px-3 py-4 space-y-1">
              {NAV_SECTIONS.map((section) => {
                const SectionIcon = section.icon;
                return (
                  <Collapsible
                    key={section.id}
                    defaultOpen={isSectionActive(section)}
                  >
                    <CollapsibleTrigger asChild>
                      <button className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100:bg-slate-700">
                        <SectionIcon className={`w-5 h-5 ${section.color}`} />
                        <span className="flex-1 text-left">{section.label}</span>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4 mt-1 space-y-1">
                      {section.items.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                              isItemActive(item.path) 
                                ? 'bg-[#0066CC]/10 text-[#0066CC] font-medium' 
                                : 'text-slate-600'
                            }`}
                          >
                            <ItemIcon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </nav>

            {/* Mobile Theme Toggle */}
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            {/* Mobile Menu Button */}
            <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </Button>

            {/* Global Search */}
            <div className="flex-1 max-w-md mx-4 hidden sm:block">
              <GlobalSearch />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Mobile Search */}
              <Button variant="ghost" size="sm" className="sm:hidden">
                <MailQuestion className="w-5 h-5" />
              </Button>

              {/* Notifications - Real-time SSE */}
              <NotificationBell />

              {/* Mobile User */}
              <Button variant="ghost" size="sm" className="lg:hidden" onClick={logout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 min-h-[calc(100vh-140px)]">
          {/* Breadcrumbs */}
          <Breadcrumbs />
          
          {/* Page Content */}
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-white px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>ENTRAJUDATA © 2026 - Gestão de Instituições</span>
            <a 
              href="/" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[#0066CC] hover:underline"
              data-testid="link-landing-page"
            >
              <ExternalLink className="w-4 h-4" />
              Aceder a outros Portais
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MainLayout;
